import { BreakpointState } from "@angular/cdk/layout";
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { filter, Observable, Subscription, tap } from "rxjs";

import {
    SECONDARY_COLOR_LIGHT,
    TRANSPARENT_COLOR,
} from "@hermes/aphrodite/expansion-panel";
import {
    Product,
    ProductColorVariant,
    ProductSizeVariant,
    ProductVolumeVariant,
} from "@hermes/api-model-product";
import { Context } from "@hermes/app-core";

import {
    ENGRAVING_OPTION_1L_2C,
    FormHelper,
    hasColorVariant,
    hasSizeVariant,
    hasVolumeVariant,
    isProductEngravable,
    isRetailOnlyStock,
} from "@hermes/fragments/product-utils";
import { AddToCartFacade, PRODUCT_PAGE } from "@hermes/states/add-to-cart";
import { ApplePayFacade, ApplepayStatus } from "@hermes/states/apple-pay";
import { AddToCartPayload, BasketFacade } from "@hermes/states/basket";
import { FeatureFlagFacade, PERFUME_ENGRAVING } from "@hermes/states/flipper";
import {
    COLOR,
    ProductPageFacade,
    SIZE,
    VOLUME,
} from "@hermes/states/product-page";

import { EcomErrorCodes } from "@hermes/utils-generic/constants";
import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

/**
 * ProductForm, should store the state and logic of opening closing/a toggle
 * or activating or not the buttons add to cart or store locator.
 */
@Component({
    selector: "h-simple-product-selector",
    templateUrl: "./simple-product-selector.component.html",
    styleUrls: ["./simple-product-selector.component.scss"],
})
export class SimpleProductSelectorComponent
    implements OnInit, OnChanges, OnDestroy
{
    @ViewChild("sizeToggle", { read: ElementRef })
    public sizeToggle: ElementRef;

    @ViewChild("ngForm", { read: ElementRef })
    public ngForm: ElementRef;

    /** Current page product */
    @Input()
    public product: Product;

    public isMobile: boolean;

    /* Form for AddToCart */
    public variantsForm: FormGroup = new FormGroup({});

    /** local color toggle state */
    public colorToggleOpened = true;
    /** local size toggle state */
    public sizeToggleOpened = false;
    /** local volume toggle state */
    public volumeToggleOpened = true;

    /** value of capa */
    public isCapacity = false;

    /** In some functional cases we have to hide size alert */
    public hideSizeAlert = false;
    public findInStorePressed = false;

    public isAddToCartButtonDisplayed = true;

    // if the user has selected a variation, store the sku here.
    // only one selected* should be defined at any time.
    public selectedColorVariant: ProductColorVariant;
    public selectedVolumeVariant: ProductVolumeVariant;
    public selectedSizeVariant: ProductSizeVariant;

    // EcomError datas
    public errorCode: string;

    public subscription: Subscription = new Subscription();

    public secondaryColorLight = SECONDARY_COLOR_LIGHT;
    public transparentColor = TRANSPARENT_COLOR;

    // Instanciate a store in parent component to save the engraving information when change variant volume (to prefill the form when comeback on it)
    // A variant volume without engraving option, delete the child component and lose the store
    public storeEngravingLineByVolumeVariant: Map<
        string,
        { [key: string]: string }
    > = new Map();
    private perfumeEngravingIsEnabled: boolean = false;

    constructor(
        public readonly productPageFacade: ProductPageFacade,
        private readonly formHelper: FormHelper,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly context: Context,
        private readonly basketFacade: BasketFacade,
        private readonly breakpointService: BreakpointService,
        private readonly featureFlagFacade: FeatureFlagFacade,
        private readonly applePayFacade: ApplePayFacade,
        private readonly addToCartFacade: AddToCartFacade,
    ) {}

    public get displayPerfumeEngravingBlock(): boolean {
        return (
            isProductEngravable(this.product) && this.perfumeEngravingIsEnabled
        );
    }

    public get isProductEngraving(): boolean {
        return Boolean(
            this.variantsForm.get("infosPerfumeEngraving")?.value?.engraving
                ?.length,
        );
    }

    public ngOnInit(): void {
        this.initOrUpdateAddToCartForm();
        // todo call refresh for each variant.

        if (this.context.isInBrowserMode()) {
            this.subscription.add(
                this.breakpointService
                    .mediumBreakpointObserver()
                    .subscribe((value: BreakpointState) => {
                        this.isMobile = !value.matches;
                        this.changeDetectorRef.detectChanges();
                    }),
            );
        }

        this.subscription.add(
            this.productPageFacade.selectedVariant(0).subscribe((variant) => {
                if (variant.color) {
                    this.selectedColorVariant = variant.color;
                }
                if (variant.volume) {
                    this.selectedVolumeVariant = variant.volume;
                }
                // eslint-disable-next-line unicorn/explicit-length-check
                if (variant.size) {
                    this.selectedSizeVariant = variant.size;
                }
            }),
        );

        this.subscription.add(
            this.checkFormWhenApplePayBtnIsClicked().subscribe(),
        );
        this.subscription.add(
            this.featureFlagFacade
                .isActivated(PERFUME_ENGRAVING)
                .subscribe(
                    (perfumeEngravingIsEnabled) =>
                        (this.perfumeEngravingIsEnabled =
                            perfumeEngravingIsEnabled),
                ),
        );
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.product && changes.product.currentValue) {
            this.initOrUpdateAddToCartForm();
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.resetProductSizeVariantAndCart();
    }

    /**
     * Force change detection when functional values are received from children.
     * In order to avoid a flippering error message with sizes.
     */
    public detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }

    public addToCartAction(showNotifSuccess: boolean = true): void {
        const sku = this.getSkuFromVariantsForm();

        // if there is a sku : form is valid and call add to cart
        if (sku) {
            const addToCartPayload: AddToCartPayload = {
                sku,
                displayNotifOnSuccess: showNotifSuccess,
                itemPosition: 0,
                templateType: this.product.templateType,
                context: PRODUCT_PAGE,
            };
            const engravingInfos = this.variantsForm.get(
                "infosPerfumeEngraving",
            )?.value as { [key: string]: string } | undefined;
            if (
                engravingInfos &&
                Object.values(engravingInfos).some((value) => value.length > 0)
            ) {
                addToCartPayload.customOptions = {
                    engraving:
                        this.product.simpleAttributes?.engravingOptions ===
                        ENGRAVING_OPTION_1L_2C
                            ? Object.values(engravingInfos).map((engraving) =>
                                  engraving.toUpperCase(),
                              )
                            : Object.values(engravingInfos),
                };
            }
            this.basketFacade.addToCart(addToCartPayload);
        }
    }

    public shouldPreventProductLocatorLaunch() {
        return (
            Boolean(this.variantsForm.get(SIZE)?.invalid) &&
            hasSizeVariant(this.product)
        );
    }

    public findInStoreAction() {
        if (this.shouldPreventProductLocatorLaunch()) {
            this.displayVariantFormError();
            this.findInStorePressed = true;
        }
    }

    /**
     * Return selected sku from form after check the validation's form.
     * If form is invalid, display error and return undefined.
     */
    public getSkuFromVariantsForm(): string | undefined {
        if (this.variantsForm.invalid) {
            // form is invalid display error and return undefined sku
            this.displayVariantFormError();
            return undefined;
        }

        // form is valid, get correct sku from form
        let sku: string;
        if (hasSizeVariant(this.product)) {
            sku = this.variantsForm.get(SIZE)?.value;
        } else if (hasVolumeVariant(this.product)) {
            sku = this.variantsForm.get(VOLUME)?.value;
        } else {
            // If no variants, no need form Validation, the product.sku value is sent to addToCart
            sku = this.product.sku;
        }

        return sku;
    }

    /**
     * In mobile view, scroll to the error message section
     */
    public scrollToError(): void {
        if (this.isMobile) {
            const element = this.sizeToggle.nativeElement as HTMLElement;
            element.scrollIntoView();
        }
    }

    /**
     * Return default error code message when add to cart fail.
     * Depends on product has size variants
     */
    public getDefaultAddToCartErrorCode(): string {
        if (hasSizeVariant(this.product)) {
            return EcomErrorCodes.SIZE_OUT_OF_STOCK;
        }
        return EcomErrorCodes.OUT_OF_STOCK;
    }

    /**
     * Store new state of size toggle and send event of clicked toggle
     *
     * @param event boolean state of toggle collapse
     */
    public handleSizesToggled(event: { id: string; value: boolean }): void {
        this.sizeToggleOpened = !event.value;
        this.productPageFacade.showMoreVariant({
            id: SIZE,
            isExpanded: this.sizeToggleOpened,
        });
    }

    /**
     * Store new state of volume toggle and send event of clicked toggle
     *
     * @param event boolean state of toggle collapse
     */
    public handleVolumesToggled(event: { id: string; value: boolean }): void {
        this.volumeToggleOpened = !event.value;
        this.productPageFacade.showMoreVariant({
            id: VOLUME,
            isExpanded: this.volumeToggleOpened,
        });
    }

    /**
     * Check is the Size Alert should be shown
     */
    public displaySizeAlert(): boolean {
        return (
            Boolean(this.variantsForm.get(SIZE)?.invalid) &&
            hasSizeVariant(this.product) &&
            !this.hideSizeAlert
        );
    }

    /**
     * Handle display border-top for add to cart button
     */
    public hasTopBorderForAddToCart(): string | boolean {
        return (
            (!hasSizeVariant(this.product) && hasColorVariant(this.product)) ||
            this.product.attributes.shippingInformation ||
            (hasVolumeVariant(this.product) && this.volumeToggleOpened) ||
            (hasSizeVariant(this.product) && this.sizeToggleOpened)
        );
    }

    /**
     * handle display border-top for shipping information
     */
    public hasTopBorderForDeliveryInformation(): boolean {
        return (
            (hasVolumeVariant(this.product) && this.volumeToggleOpened) ||
            (hasSizeVariant(this.product) && this.sizeToggleOpened) ||
            (hasColorVariant(this.product) &&
                this.colorToggleOpened &&
                !hasSizeVariant(this.product))
        );
    }

    private cancelApplePayProcessAndShowErrorMessage(): void {
        this.applePayFacade.abort();
        this.displayVariantFormError();
    }

    private checkFormWhenApplePayBtnIsClicked(): Observable<void | ApplepayStatus> {
        return this.applePayFacade.status$.pipe(
            filter((status) => status === ApplepayStatus.CheckingForm),
            tap(() =>
                this.variantsForm.invalid
                    ? this.cancelApplePayProcessAndShowErrorMessage()
                    : this.applePayFacade.formChecked(),
            ),
        );
    }

    private displayVariantFormError(): void {
        this.hideSizeAlert = false;
        this.sizeToggleOpened = true;
        this.scrollToError();
        if (this.ngForm) {
            this.formHelper.focusInvalidInput(this.ngForm);
        }
    }

    /**
     * Init a FormGroup with a FormBuilder, controls are added depending of the product variants
     * Each new FormControl add a default Validators to set dynamically the required fields
     */
    private initOrUpdateAddToCartForm(): void {
        this.hideSizeAlert = true;
        if (this.product.stock) {
            this.isAddToCartButtonDisplayed = !isRetailOnlyStock(
                this.product.stock,
            );
        }

        this.addOrUpdateColorFromControl();
        this.addOrUpdateSizeFormControl();
        this.addOrUpdateVolumeFormControl();

        // If no variants, no need for form Validation, the product.sku value is sent to addToCart
    }

    private addOrUpdateColorFromControl(): void {
        if (hasColorVariant(this.product)) {
            const colorFormControlExist = this.variantsForm.contains(COLOR);
            if (colorFormControlExist) {
                this.variantsForm.controls[COLOR].setValue(
                    this.product.simpleAttributes?.refColoris,
                );
            } else {
                this.variantsForm.addControl(
                    COLOR,
                    new FormControl(this.product.simpleAttributes?.refColoris),
                );
            }

            if (colorFormControlExist) {
                this.variantsForm.removeControl(SIZE);
                this.variantsForm.removeControl(VOLUME);
            }
        }
    }

    private addOrUpdateSizeFormControl(): void {
        if (hasSizeVariant(this.product)) {
            const sizeFormControlExist = this.variantsForm.contains(SIZE);
            if (sizeFormControlExist) {
                this.variantsForm.controls[SIZE].setValue("");
            } else {
                this.variantsForm.addControl(
                    SIZE,
                    new FormControl("", Validators.required),
                );
            }

            if (sizeFormControlExist) {
                this.variantsForm.removeControl(COLOR);
                this.variantsForm.removeControl(VOLUME);
            }
        }
    }

    private addOrUpdateVolumeFormControl(): void {
        if (hasVolumeVariant(this.product)) {
            const volumeFromControl = this.variantsForm.contains(VOLUME);
            if (volumeFromControl) {
                this.variantsForm.controls[VOLUME].setValue(this.product.sku);
            } else {
                this.variantsForm.addControl(
                    VOLUME,
                    new FormControl(this.product.sku),
                );
            }

            if (volumeFromControl) {
                this.variantsForm.removeControl(SIZE);
                this.variantsForm.removeControl(COLOR);
            }
        }
    }

    private resetProductSizeVariantAndCart(): void {
        this.productPageFacade.resetSizeSelectedVariants();
        this.addToCartFacade.changeButtonEnabledState(0, true);
    }
}
